import type { RankingEntry } from '../../shared/types/game.types';
import { Pagination } from '../common/Pagination';
import { TitleBadge } from '../TitleBadge';
import { TrophyBadge } from '../TrophyBadge';
import type { UserRankWithBadge } from '../../services/userRankService';
import { useEffect, useRef, useState } from 'react';

interface RankingPanelProps {
  visible: boolean;
  rankings: RankingEntry[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onClose: () => void;
  formatBalance: (value: number) => string;
  onAccountClick?: (
    userKey: number,
    nickname: string,
    isPublicProfile: boolean
  ) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  rankingsAsOf?: string | null;
  myUserKey?: number | null;
  myRank?: UserRankWithBadge | null;
  onViewMyRank?: () => void;
  myRankFlashToken?: number;
}

export const RankingPanel = ({
  visible,
  rankings,
  isLoading,
  error,
  onRefresh,
  onClose,
  formatBalance,
  onAccountClick,
  currentPage,
  totalPages,
  onPageChange,
  rankingsAsOf,
  myUserKey,
  myRank,
  onViewMyRank,
  myRankFlashToken,
}: RankingPanelProps) => {
  const myRowRef = useRef<HTMLTableRowElement | null>(null);
  const [flashActive, setFlashActive] = useState(false);

  useEffect(() => {
    if (!myRankFlashToken) return;
    // 버튼 클릭으로 토큰이 바뀌면 한 번만 반짝이게
    setFlashActive(true);
    const id = window.setTimeout(() => setFlashActive(false), 700);
    return () => window.clearTimeout(id);
  }, [myRankFlashToken]);

  useEffect(() => {
    if (!flashActive || !myUserKey) return;
    // 페이지 이동 후 내 row가 렌더되면 중앙으로 스크롤
    const id = window.setTimeout(() => {
      myRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(id);
  }, [flashActive, currentPage, myUserKey]);

  if (!visible) {
    return null;
  }

  const getRankDisplay = (rank: number, badge: any) => {
    if (badge) {
      return <TrophyBadge rank={rank} size="small" title={badge.name} />;
    }
    return <span className="ranking-rank">{rank}</span>;
  };

  const getProfitRateColor = (profitRate: number) => {
    if (profitRate > 0) return '#22c55e'; // 초록색
    if (profitRate < 0) return '#ef4444'; // 빨간색
    return '#6b7280'; // 회색
  };

  return (
    <section className="trading-history-section">
      <div className="trading-history-header">
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            width: '100%',
            padding: '0.6rem clamp(1rem, 4vw, 2rem)',
            boxSizing: 'border-box',
            margin: 0,
          }}
        >
          <div
            className="trading-history-title"
            style={{ flex: '1 1 70%', minWidth: 0, padding: '0.6rem 0' }}
          >
            <span
              className="trading-history-icon"
              style={{ marginLeft: '1.2rem' }}
            >
              🏆
            </span>
            <div style={{ marginLeft: '0.75rem' }}>
              <h2>플레이어 랭킹</h2>
              <p>현재까지 기록된 사용자 자산과 게임 횟수</p>
              {rankingsAsOf && (
                <p style={{ marginTop: 4, fontSize: 12, opacity: 0.75 }}>
                  랭킹 기준 시각: {new Date(rankingsAsOf).toLocaleString('ko-KR')}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="logout-btn"
              style={{
                marginLeft: 'auto',
                marginRight: '1.2rem',
              }}
            >
              ← 게임 화면으로
            </button>
          </div>
        </div>
      </div>
      <div
        className="trading-history-body"
        style={{
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: '0.5rem',
          paddingBottom: '1.5rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            width: '100%',
            padding: '0 clamp(1rem, 4vw, 2rem)',
            boxSizing: 'border-box',
          }}
        >
          {isLoading ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div className="loading-state" style={{ margin: '0 auto' }}>
                <div className="loading-spinner">⏳</div>
                <p>랭킹을 불러오는 중...</p>
              </div>
            </div>
          ) : error ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div className="error-state" style={{ margin: '0 auto' }}>
                <div className="error-icon">❌</div>
                <h3>오류가 발생했습니다</h3>
                <p>{error}</p>
              </div>
            </div>
          ) : rankings.length === 0 ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div className="empty-state" style={{ margin: '0 auto' }}>
                <div className="empty-icon">🏆</div>
                <h3>랭킹 데이터가 없습니다</h3>
                <p>아직 랭킹 데이터가 없습니다.</p>
              </div>
            </div>
          ) : (
            <div
              className="trading-session-list"
              style={{
                width: '100%',
                maxWidth: '1200px',
                margin: '0 auto',
              }}
            >
              <div className="session-list-header" style={{ padding: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                    <h3 style={{ margin: 0 }}>플레이어 랭킹</h3>
                    <p style={{ margin: 0, opacity: 0.8 }}>
                      {rankings.length}명 표시 중
                    </p>
                  </div>

                  {myUserKey && onViewMyRank && (
                    <button className="logout-btn" onClick={onViewMyRank}>
                      내 랭킹 보기
                    </button>
                  )}
                </div>
              </div>

              <div
                className="session-table-container"
                style={{ width: '100%', maxWidth: '100%' }}
              >
                <table className="session-table">
                  <thead>
                    <tr>
                      <th>순위</th>
                      <th>플레이어</th>
                      <th>자산</th>
                      <th>수익률</th>
                      <th>게임수</th>
                      <th>공개</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map(entry => {
                      const isMe = !!myUserKey && entry.userKey === myUserKey;
                      const isPublic = entry.isPublicProfile ?? false;
                      // 디버깅: isPublicProfile 값 확인 (이메일 제거)
                      if (rankings.indexOf(entry) < 3) {
                        console.log('랭킹 항목:', {
                          nickname: entry.nickname,
                          isPublicProfile: entry.isPublicProfile,
                          isPublic,
                          hasOnAccountClick: !!onAccountClick,
                        });
                      }
                      // 로그인한 사용자만 다른 사람(또는 본인) 상세로 이동 가능
                      const canClick = !!onAccountClick && !!myUserKey;

                      return (
                        <tr
                          key={entry.rank}
                          className="session-table-row"
                          ref={isMe ? myRowRef : undefined}
                          style={{
                            cursor: canClick ? 'pointer' : 'default',
                            opacity: canClick ? 1 : 0.6,
                            backgroundColor: isMe ? 'rgba(59, 130, 246, 0.10)' : undefined,
                          }}
                          data-is-me={isMe ? 'true' : 'false'}
                          data-flash={isMe && flashActive ? 'true' : 'false'}
                          onClick={e => {
                            if (canClick && onAccountClick) {
                              e.stopPropagation();
                              // 이메일 로그 제거 (민감 정보 보호)
                              console.log(
                                '랭킹 행 클릭:',
                                '공개:',
                                isPublic,
                                '닉네임:',
                                entry.nickname
                              );
                              onAccountClick(entry.userKey, entry.nickname, isPublic);
                            } else {
                              console.log('랭킹 행 클릭 불가:', {
                                canClick,
                                hasOnAccountClick: !!onAccountClick,
                                isPublic,
                                isPublicProfile: entry.isPublicProfile,
                              });
                            }
                          }}
                          onMouseEnter={e => {
                            if (canClick) {
                              e.currentTarget.style.backgroundColor =
                                'var(--bg-tertiary)';
                            }
                          }}
                          onMouseLeave={e => {
                            if (canClick) {
                              e.currentTarget.style.backgroundColor =
                                'transparent';
                            }
                          }}
                          title={
                            !canClick
                              ? '로그인 후 확인할 수 있어요'
                              : !isPublic
                              ? '비공개 프로필 - 매매내역을 볼 수 없습니다'
                              : '매매내역 보기 (클릭)'
                          }
                        >
                          <td>
                            <span className="ranking-rank-cell">
                              {getRankDisplay(entry.rank, entry.badge)}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                color: canClick
                                  ? 'var(--accent-color)'
                                  : 'var(--text-secondary)',
                                fontWeight: canClick ? 600 : 400,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                              }}
                            >
                              {entry.selectedTitle && (
                                <TitleBadge
                                  title={entry.selectedTitle}
                                  size="small"
                                />
                              )}
                              {entry.nickname}
                            </span>
                          </td>
                          <td>{formatBalance(entry.balance)}</td>
                          <td>
                            <span
                              style={{
                                color: getProfitRateColor(entry.profitRate),
                              }}
                            >
                              {entry.profitRateFormatted}
                            </span>
                          </td>
                          <td>{entry.gameCount.toLocaleString()}회</td>
                          <td>
                            <span
                              style={{
                                color: isPublic ? '#22c55e' : '#6b7280',
                                fontSize: '0.85rem',
                                fontWeight: isPublic ? 600 : 400,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                              }}
                            >
                              {isPublic ? (
                                <>
                                  <span>🔓</span>
                                  <span>공개</span>
                                </>
                              ) : (
                                <>
                                  <span>🔒</span>
                                  <span>비공개</span>
                                </>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
